<template>
  <div v-if="student && requirement">
    <!-- Submitted -->
    <div v-if="value && value.submissionDate">
      <div v-if="value.waived">
        <router-link
          class="my-auto"
          style="text-decoration: none"
          :to="{
            name: 'studentRequirementSubmissionView',
            params: { id: value.submissionId },
          }"
        >
          Waived on {{ value.submissionDate | formatDateYear }}
        </router-link>
      </div>

      <div v-else class="d-flex justify-space-between flex-column flex-sm-row">
        <router-link
          class="my-auto"
          style="text-decoration: none"
          :to="{
            name: 'studentRequirementSubmissionView',
            params: { id: value.submissionId },
          }"
        >
          <span v-if="requirement.type == 'Vaccination'"
            >Dose {{ forDoseNumber }} submitted</span
          >
          <span v-else>Submitted</span>
          on {{ value.submissionDate | formatDateYear }}
        </router-link>

        <div
          class="font-weight-light"
          v-if="requirement.type == 'Certification'"
        >
          {{ certificationPeriod(requirement) }}
        </div>
        <div v-if="value.state == 'pending'">
          <v-btn disabled text>Pending</v-btn>
        </div>
      </div>
    </div>

    <!-- NOT Submitted -->
    <div class="d-flex justify-space-between flex-column flex-sm-row" v-else>
      <span class="my-auto" v-if="requirement.type == 'Vaccination'"
        >Dose {{ forDoseNumber }} due on
        {{ value.dueDate | formatDateYear }}</span
      >
      <span class="my-auto" v-if="requirement.type == 'Certification'">
        <span v-if="value.dueDate">
          Due on {{ value.dueDate | formatDateYear }}
        </span>
        <span> No record </span>
      </span>
      <span class="my-auto" v-if="requirement.type == 'Required Documents'">
        No record
      </span>

      <div class="d-flex">
        <v-checkbox
          v-model="value.selected"
          @change="
            $emit('selectionChanged', {
              state: value.selected,
              requirement,
              forDoseNumber,
            })
          "
          class="my-auto ml-auto mt-2 mt-md-0"
          v-if="selectMode"
          hide-details
        />
        <div v-else class="d-flex">
          <v-btn
            color="primary"
            :disabled="disabled"
            class="mx-0 my-auto mt-2 mt-md-0 px-0 mx-md-2"
            :to="{
              path: `/studentRequirementSubmission/create`,
              query: {
                studentId: student.id,
                requirements: `${requirement.requirementId}_${forDoseNumber}`,
              },
            }"
            text
          >
            Submit
          </v-btn>
          <v-menu offset-y>
            <template v-slot:activator="{ on, attrs }">
              <v-btn class="ml-2 mt-2 mt-md-0" icon v-bind="attrs" v-on="on">
                <v-icon>mdi-dots-vertical</v-icon>
              </v-btn>
            </template>
            <v-card class="mx-auto" max-width="400" width="200" tile>
              <v-list dense>
                <v-list-item-group color="primary">
                  <v-list-item
                    @click="waiveRequirement(requirement, forDoseNumber)"
                  >
                    <v-list-item-content>
                      <v-list-item-title>Waive Requirement</v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                  <v-list-item
                    @click="viewOnlineSubmissions(requirement)"
                    v-if="requirement.formId"
                  >
                    <v-list-item-content>
                      <v-list-item-title>
                        Choose Online Submission
                      </v-list-item-title>
                    </v-list-item-content>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </v-card>
          </v-menu>
        </div>
      </div>
    </div>

    <v-dialog v-if="requirement" v-model="onlineSubmissionsDialog">
      <v-card>
        <v-card-actions class="justify-center">
          <v-btn class="mt-2" @click="onlineSubmissionsDialog = false" text
            >Close</v-btn
          >
        </v-card-actions>
        <v-card-title>Online Submissions</v-card-title>
        <v-card-text>
          <select-online-submission
            @selected="selectedOnlineFormSubmission"
            :student="student"
            :requirement="requirement"
          />
        </v-card-text>
      </v-card>
    </v-dialog>
    <confirmation-dialog ref="confirm" />
  </div>
</template>

<script>
const SelectOnlineSubmission = () =>
  import("@/components/requirement/SelectOnlineSubmission.vue");
import { mapActions } from "vuex";
const ConfirmationDialog = () =>
  import("@/components/common/ConfirmationDialog.vue");

export default {
  props: {
    value: {
      type: Object,
      required: false,
    },
    requirement: {
      type: Object,
      required: true,
    },
    student: {
      type: Object,
      required: true,
    },
    selectMode: {
      type: Boolean,
      required: false,
      default: false,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  components: { SelectOnlineSubmission, ConfirmationDialog },
  data: () => ({
    onlineForm: null,
    onlineSubmissionsDialog: false,
  }),
  computed: {
    forDoseNumber() {
      return this.value && this.value.dose ? this.value.dose : 1;
    },
  },
  created() {},
  methods: {
    ...mapActions("messages", [
      "addMessage",
      "addErrorMessage",
      "addSuccessMessage",
    ]),
    viewProof() {
      console.log("view proof...");
    },
    certificationPeriod(requirement) {
      if (
        requirement &&
        requirement.studentSubmissionRequirements &&
        requirement.studentSubmissionRequirements.length > 0
      ) {
        const submission = requirement.studentSubmissionRequirements[0];
        const issueDate = this.$options.filters.formatDateYear(
          submission.cerfIssueDate
        );
        const expireDate = this.$options.filters.formatDateYear(
          submission.cerfExpireDate
        );
        return `${issueDate} - ${expireDate}`;
      }
    },
    async initForm() {
      if (this.requirement.formId) {
        const params = {
          form_id: this.requirement.formId,
        };
        return await this.$axios.get("/form/get-form", params).then((resp) => {
          this.onlineForm = resp.data.data;
        });
      }
    },
    waiveRequirement(requirement, forDoseNumber) {
      this.$router.push({
        name: "waiveStudentRequirementForm",
        query: {
          studentId: this.student.id,
          requirements: `${requirement.requirementId}_${forDoseNumber}`,
        },
      });
    },
    viewOnlineSubmissions() {
      this.onlineSubmissionsDialog = true;
    },
    async selectedOnlineFormSubmission({ submission, requirement }) {
      //Check if any submission RP id or student id matches the current student
      if (submission.studentId && this.student.id != submission.studentId) {
        let confirm = await this.$refs.confirm.open(
          "Warning",
          "The selected online submission is associated with another student, are you sure you want to continue?"
        );
        if (!confirm) {
          return;
        }
      }

      this.onlineSubmissionsDialog = false;

      const data = {
        studentId: this.student.id,
        formSubmissionId: submission.id,
        requirements: [
          {
            requirementId: requirement.requirementId,
            // forDoseNumber: null,
            // vaccinationDate: null,
            // cerfIssueDate: null,
            // cerfExpireDate: null,
          },
        ],
      };
      this.$axios
        .post("/studentRequirementSubmission/assign-form", data)
        .then(() => {
          this.addSuccessMessage("Assigned form submission to requirement");
          this.$emit('statusChanged');
        });
    },
  },
};
</script>

<style>
</style>